import { DocumentNode, gql } from '@apollo/client';
import { Avatar, Box, HStack } from '@chakra-ui/react';
import NextLink from 'next/link';
import { useRouter } from 'next/router';
import * as React from 'react';

import { AbbreviateRedraft } from '@/components/AbbreviateRedraft';
import { AttachmentDisplay } from '@/components/AttachmentDisplay';
import {
  COMMENT_QUERY_PARAMETER_NAME,
  REPLY_TO_COMMENT_NAME,
} from '@/components/Post/components/Comments/utils';
import { EditComment } from '@/components/Post/components/EditComment';
import {
  PlatformContextPreviewLink,
  useLinkPreview,
} from '@/imports/ui/components/context/PlatformContextPreviewLink';

import { Comment_CommentFragment } from './graphql-types/Comment_CommentFragment';

import { Header } from './components/Header';
import { ImageDisplay } from './components/ImageDisplay';
import { LikeReplyFooter } from './components/LikeReplyFooter';
import { ModerationHeader } from './components/ModerationHeader';
import { useModeration } from './hooks/useModeration';

export const Comment: React.FC<{
  comment: Comment_CommentFragment;
  onClickReply: () => void;
  onClickReplyCount?: () => void;
}> & {
  commentFragment: DocumentNode;
} = ({ comment, onClickReply, onClickReplyCount, children }) => {
  const { linkPreview } = useLinkPreview({
    rawDraftContentState: comment.bodyContentState,
  });
  const [showEditComment, setShowEditComment] = React.useState(false);
  const router = useRouter();

  const [emphasizeComment, setEmphasizeComment] = React.useState(false);

  React.useEffect(() => {
    if (router.isReady) {
      const queryParamsCommentId = router.query[COMMENT_QUERY_PARAMETER_NAME];
      const replyToComment = router.query[REPLY_TO_COMMENT_NAME];

      setEmphasizeComment(
        comment._id === queryParamsCommentId || comment._id === replyToComment
      );
    }
  }, [comment._id, router]);

  const commentRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (emphasizeComment) {
      commentRef?.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }
  }, [commentRef, emphasizeComment]);

  const { shouldHideComment, shouldShowHeader } = useModeration(comment);

  return shouldHideComment ? null : (
    <HStack align="start" width="full" ref={commentRef}>
      <NextLink href={`/members/${comment.author._id}`}>
        <Avatar
          width={comment.comment ? 8 : 10}
          height={comment.comment ? 8 : 10}
          name={comment.author.displayName}
          src={comment.author.proxyProfilePictureUrl || undefined}
        />
      </NextLink>
      <Box width="full">
        {showEditComment ? (
          <EditComment
            comment={comment}
            onClose={() => setShowEditComment(false)}
          />
        ) : (
          <>
            <Box
              backgroundColor={`${emphasizeComment ? 'gray.100' : 'gray.50'}`}
              px="4"
              py="2"
              borderRadius="xl"
              width="full"
            >
              {shouldShowHeader && <ModerationHeader comment={comment} />}
              <Header
                comment={comment}
                mb="1"
                onClickEdit={() => setShowEditComment(true)}
              />
              <AbbreviateRedraft
                rawDraftContentState={comment.bodyContentState}
                noOfCharacters={300}
              />
            </Box>
            <PlatformContextPreviewLink
              linkPreview={linkPreview}
              canEdit={false}
              onClose={() => null}
              mt="2"
              w="full"
            />
            <ImageDisplay
              commentEmbeddedImage={comment.embeddedImages}
              mt="2"
            />
            <AttachmentDisplay versionFiles={comment.versionFiles} mt="2" />
            <LikeReplyFooter
              comment={comment}
              width="full"
              mt="1"
              onClickReply={onClickReply}
              onClickReplyCount={onClickReplyCount}
            />
          </>
        )}
        {children}
      </Box>
    </HStack>
  );
};

Comment.commentFragment = gql`
  fragment Comment_CommentFragment on Comment {
    _id
    currentUserIsCommentAuthor
    author {
      _id
      displayName
      proxyProfilePictureUrl(
        input: { w: 140, h: 140, fit: "facearea", facepad: 10.0 }
      )
    }
    bodyContentState {
      ...useLinkPreviewFragment
    }
    ...Header_CommentFragment
    ...LikeReplyFooter_CommentFragment
    ...EditComment_CommentFragment
    ...ModerationHeader_CommentFragment
    ...useModeration_CommentFragment
    versionFiles {
      _id
      ...AttachmentDisplay_VersionFileFragment
    }
    embeddedImages {
      ...ImageDisplay_CommentEmbeddedImageFragment
    }
  }
  ${LikeReplyFooter.commentFragment}
  ${EditComment.commentFragment}
  ${ImageDisplay.commentEmbeddedImageFragment}
  ${ModerationHeader.commentFragment}
  ${useModeration.commentFragment}
  ${AttachmentDisplay.versionFileFragment}
  ${Header.commentFragment}
  ${useLinkPreview.rawDraftContentStateFragment}
`;
